import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { axiosApi } from "config";
import { toast } from "react-toastify";
import {
  FiCheck,
  FiArrowRight,
  FiCalendar,
  FiPieChart,
  FiGrid,
  FiClock,
  FiTrendingUp,
  FiUsers,
  FiEdit,
  FiSettings,
  FiUser,
  FiCreditCard,
} from "react-icons/fi";
import Navbar from "layouts/Navbar";
import Footer from "layouts/Footer";
import Landing1Img from "assets/img/landing/landing-1.png";
import Landing2Img from "assets/img/landing/landing-2.png";
import Landing3Img from "assets/img/landing/landing-3.png";
import SettingImg from "assets/img/landing/setting.png";
import { useNavigate } from "react-router-dom";

import SubscriptionPlans from "./admin/profile/components/stripe/SubscriptionPlans";

import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/effect-coverflow";

const LandingPage = () => {
  const [selectedPlan, setSelectedPlan] = useState("monthly");
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();

  const features = [
    {
      icon: <FiEdit className="h-8 w-8" />,
      title: "Unified Post Creation",
      description:
        "Create and schedule posts for multiple platforms from a single interface with our intuitive editor.",
    },
    {
      icon: <FiSettings className="h-8 w-8" />,
      title: "Advanced Settings Control",
      description:
        "Manage your social accounts, time zones, and posting schedules with comprehensive settings.",
    },
    {
      icon: <FiUser className="h-8 w-8" />,
      title: "Profile Management",
      description:
        "Easy profile customization and security settings including 2FA and activity monitoring.",
    },
    {
      icon: <FiCreditCard className="h-8 w-8" />,
      title: "Subscription Management",
      description:
        "Flexible subscription plans with easy billing management and payment tracking.",
    },
    {
      icon: <FiGrid className="h-8 w-8" />,
      title: "Multi-Platform Management",
      description:
        "Manage all your social media accounts from a single dashboard with ease and efficiency.",
    },
    {
      icon: <FiCalendar className="h-8 w-8" />,
      title: "Smart Scheduling",
      description:
        "Schedule posts ahead of time and optimize posting times for maximum engagement.",
    },
    {
      icon: <FiPieChart className="h-8 w-8" />,
      title: "Advanced Analytics",
      description:
        "Get detailed insights into your social media performance with comprehensive analytics.",
    },
    {
      icon: <FiClock className="h-8 w-8" />,
      title: "Auto-Posting",
      description:
        "Set up automated posting schedules to maintain consistent social media presence.",
    },
    {
      icon: <FiTrendingUp className="h-8 w-8" />,
      title: "Performance Tracking",
      description:
        "Track engagement, reach, and growth across all your social media platforms.",
    },
    {
      icon: <FiUsers className="h-8 w-8" />,
      title: "Team Collaboration",
      description:
        "Work seamlessly with your team members with role-based access and workflows.",
    },
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Connect Your Accounts",
      description:
        "Link your social media accounts in just a few clicks and start managing everything from one place.",
    },
    {
      step: "2",
      title: "Create & Schedule Content",
      description:
        "Create engaging content and schedule it to be posted at optimal times across all platforms.",
    },
    {
      step: "3",
      title: "Analyze & Optimize",
      description:
        "Track performance metrics and optimize your strategy based on detailed analytics.",
    },
  ];

  const testimonials = [
    {
      quote:
        "This platform has completely transformed how we manage our social media presence. The analytics are incredible!",
      author: "Sarah Johnson",
      role: "Marketing Director",
      company: "TechCorp",
    },
    {
      quote:
        "The scheduling features save us hours every week. It's like having an extra team member dedicated to social media.",
      author: "Michael Chen",
      role: "Social Media Manager",
      company: "GrowthStart",
    },
    {
      quote:
        "The best social media management tool we've used. The interface is intuitive and the features are powerful.",
      author: "Emma Davis",
      role: "Content Creator",
      company: "CreativeHub",
    },
  ];

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axiosApi.get("/api/plans");
        setPlans(response.data.plans);
      } catch (error) {
        console.error("Error fetching plans:", error);
        toast.error("Failed to load plans."); // Notify user (optional)
      }
    };

    fetchPlans();
  }, []);

  return (
    <>
      <Navbar />
      <div className="min-h-screen">
        {/* Hero Section */}
        <div className="container mx-auto px-6 pb-4 pt-32">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
            {/* Left Column - Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col justify-center lg:col-span-1" // Takes up 2 columns
            >
              <h1 className="mb-8 text-3xl font-bold text-gray-900 md:text-4xl">
                Manage Your Social Media <br />
                <span className="text-transparent bg-gradient-to-r from-[#7F03E2] to-[#A13BF2] bg-clip-text text-2xl md:text-3xl">
                  Like Never Before
                </span>
              </h1>
              <p className="mb-12 max-w-2xl text-xl text-gray-600">
                One platform to manage all your social media accounts, schedule
                posts, and analyze performance.
              </p>
              <div className="flex gap-4">
                <Link
                  to="/main/default"
                  className="bg-transparent hover:border-transparent group relative inline-flex items-center justify-center overflow-hidden rounded-full border-2 border-purple-500 px-8 py-4 font-semibold text-purple-600 transition-all duration-300 ease-in-out hover:text-white"
                >
                  <span className="absolute inset-0 z-0 h-full w-full translate-y-full transform bg-gradient-to-r from-[#7F03E2] to-[#A13BF2] transition-transform duration-300 ease-in-out group-hover:translate-y-0" />
                  <span className="relative flex items-center gap-2">
                    Get Started Free
                    <motion.span
                      initial={{ x: 0 }}
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      →
                    </motion.span>
                  </span>
                </Link>
              </div>
            </motion.div>

            {/* Right Column - Dashboard Image */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.2 }}
              className="relative lg:col-span-2"
            >
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{
                  duration: 0.8,
                  type: "spring",
                  stiffness: 100,
                }}
                className="inset-0 z-30 h-fit w-full rounded-xl shadow-2xl"
              >
                <img
                  src={Landing1Img}
                  alt="Dashboard Preview"
                  className="w-full rounded-xl object-cover"
                />
              </motion.div>

              {/* Background Gradient */}
              {/* <div className="absolute left-1/2 top-1/2 h-[300px] w-[100%] -translate-x-1/2 -translate-y-1/2 transform rounded-full bg-gradient-to-r from-purple-100/50 to-purple-50/50 blur-3xl" /> */}
            </motion.div>
          </div>
        </div>
        <section
          id="features"
          className="bg-gradient-to-br from-purple-50 via-white to-purple-50 py-20"
        >
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-16 text-center"
            >
              <h2 className="text-transparent mb-4 bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text text-4xl font-bold">
                Powerful Features for Social Media Success
              </h2>
            </motion.div>

            <Swiper
              spaceBetween={30}
              slidesPerView={1}
              autoplay={{
                delay: 3000,
                disableOnInteraction: false,
              }}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              breakpoints={{
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 3 },
              }}
              modules={[Autoplay, Pagination]}
              className="features-swiper !pb-14"
            >
              {features.map((feature, index) => (
                <SwiperSlide key={index}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="group relative h-[250px] rounded-2xl p-6 transition-all duration-300 hover:scale-[1.02]"
                  >
                    {/* Gradient background with animation */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white to-purple-50 shadow-lg transition-all duration-300 group-hover:shadow-xl" />

                    {/* Animated border */}
                    <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-purple-500 via-pink-500 to-purple-500 opacity-0 transition-opacity duration-300 group-hover:opacity-10" />

                    {/* Content */}
                    <div className="relative z-10 flex h-full flex-col">
                      <div className="mb-6 w-fit rounded-full bg-gradient-to-r from-purple-500 to-pink-500 p-3">
                        <div className="text-white">{feature.icon}</div>
                      </div>

                      <h3 className="mb-4 text-2xl font-bold text-gray-800 transition-colors duration-300 group-hover:text-purple-600">
                        {feature.title}
                      </h3>

                      <p className="leading-relaxed text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </section>

        {/* How It Works Section */}
        <section
          id="how-it-works"
          className="bg-gradient-to-b from-purple-50 to-white py-20"
        >
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-16 text-center"
            >
              <h2 className="mb-4 text-4xl font-bold text-gray-900">
                How It Works
              </h2>
              <p className="mx-auto max-w-2xl text-xl text-gray-600">
                Get started in minutes with our simple setup process
              </p>
            </motion.div>

            <div className="grid gap-8 md:grid-cols-3">
              {howItWorks.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="relative"
                >
                  <div className="relative z-10 rounded-lg bg-gradient-to-b from-white to-purple-50 p-8">
                    <div className="absolute -left-6 -top-6 text-5xl font-bold text-purple-200">
                      {step.step}
                    </div>
                    <h3 className="mb-4 text-xl font-semibold">{step.title}</h3>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                  {index < howItWorks.length - 1 && (
                    <div className="absolute right-0 top-1/2 hidden -translate-y-1/2 translate-x-3/4 transform md:block">
                      <FiArrowRight className="h-6 w-6 text-purple-300" />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        <section className="relative overflow-hidden bg-gradient-to-br from-white via-purple-50 to-white py-20">
          <motion.div
            className="absolute inset-0 opacity-30"
            animate={{
              background: [
                "radial-gradient(circle at 0% 0%, #7F03E2 0%, transparent 50%)",
                "radial-gradient(circle at 100% 100%, #A13BF2 0%, transparent 50%)",
                "radial-gradient(circle at 0% 100%, #7F03E2 0%, transparent 50%)",
              ],
            }}
            transition={{ duration: 10, repeat: Infinity }}
          />
          <div className="container relative mx-auto w-full px-6 md:w-[85%]">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.8,
                  type: "spring",
                  bounce: 0.4,
                }}
                className="flex flex-col justify-center"
              >
                <motion.div
                  initial={{ scale: 0.9 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="mb-6 bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text"
                >
                  <h2 className="text-transparent text-4xl font-bold">
                    Create Posts Effortlessly
                  </h2>
                </motion.div>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-8 text-xl text-gray-600"
                >
                  Write once, publish everywhere. Our unified post creator lets
                  you manage content across all your social platforms
                  seamlessly.
                </motion.p>
                {/* <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/main/posts/new"
                    className="group relative inline-flex w-fit items-center overflow-hidden rounded-full bg-gradient-to-r from-purple-600 to-pink-500 px-8 py-3 text-white transition-all duration-300"
                  >
                    <span className="absolute left-0 h-full w-0 bg-gradient-to-r from-pink-500 to-purple-600 transition-all duration-300 group-hover:w-full" />
                    <span className="relative flex items-center">
                      Try Post Creator
                      <motion.span
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <FiArrowRight className="ml-2" />
                      </motion.span>
                    </span>
                  </Link>
                </motion.div> */}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, rotateY: 90 }}
                whileInView={{ opacity: 1, rotateY: 0 }}
                transition={{ duration: 0.8 }}
                className="relative col-span-2"
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="rounded-xl shadow-2xl"
                >
                  <img
                    src={Landing2Img}
                    alt="Post Creator Interface"
                    className="rounded-xl"
                  />
                  <motion.div
                    className="absolute inset-0 rounded-xl"
                    animate={{
                      boxShadow: [
                        "0 0 0 0 rgba(127, 3, 226, 0)",
                        "0 0 0 10px rgba(127, 3, 226, 0.1)",
                        "0 0 0 20px rgba(127, 3, 226, 0)",
                      ],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>
        {/* Settings Management Section */}
        <section className="relative overflow-hidden bg-white py-20">
          <motion.div
            className="absolute inset-0"
            animate={{
              background: [
                "linear-gradient(135deg, rgba(127, 3, 226, 0.1) 0%, transparent 100%)",
                "linear-gradient(135deg, transparent 0%, rgba(161, 59, 242, 0.1) 100%)",
              ],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
          <div className="container relative mx-auto w-full px-6 md:w-[85%]">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
              <motion.div
                initial={{ opacity: 0, x: 100 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.8,
                  type: "spring",
                  bounce: 0.4,
                }}
                className="order-2 col-span-2 rounded-xl shadow-2xl  lg:order-1"
              >
                <motion.div whileHover={{ scale: 1.02 }} className="relative">
                  <img
                    src={SettingImg}
                    alt="Settings Interface"
                    className="rounded-xl"
                  />
                  <motion.div
                    className="absolute inset-0 rounded-xl"
                    animate={{
                      boxShadow: [
                        "0 0 0 0 rgba(161, 59, 242, 0)",
                        "0 0 0 10px rgba(161, 59, 242, 0.1)",
                        "0 0 0 20px rgba(161, 59, 242, 0)",
                      ],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.8,
                  type: "spring",
                  bounce: 0.4,
                }}
                className="order-1 flex flex-col justify-center lg:order-2"
              >
                <motion.div
                  initial={{ scale: 0.9 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="mb-6 bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text"
                >
                  <h2 className="text-transparent text-4xl font-bold">
                    Stay Organized with Our Smart Calendar
                  </h2>
                </motion.div>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-8 text-xl text-gray-600"
                >
                  Plan, schedule, and manage your social media posts effortlessly with our intuitive calendar. Keep your content strategy on track with ease.
                </motion.p>
                {/* <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/main/profile/post-scheduling-preferences"
                    className="group relative inline-flex w-fit items-center overflow-hidden rounded-full bg-gradient-to-r from-purple-600 to-pink-500 px-8 py-3 text-white transition-all duration-300"
                  >
                    <span className="absolute left-0 h-full w-0 bg-gradient-to-r from-pink-500 to-purple-600 transition-all duration-300 group-hover:w-full" />
                    <span className="relative flex items-center">
                      Explore Settings
                      <motion.span
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <FiArrowRight className="ml-2" />
                      </motion.span>
                    </span>
                  </Link>
                </motion.div> */}
              </motion.div>
            </div>
          </div>
        </section>
        <section className="relative overflow-hidden bg-gradient-to-br from-white via-purple-50 to-white py-20">
          <motion.div
            className="absolute inset-0 opacity-30"
            animate={{
              background: [
                "radial-gradient(circle at 0% 0%, #7F03E2 0%, transparent 50%)",
                "radial-gradient(circle at 100% 100%, #A13BF2 0%, transparent 50%)",
              ],
            }}
            transition={{ duration: 10, repeat: Infinity }}
          />
          <div className="container relative mx-auto w-full px-6 md:w-[85%]">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-3">
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.8,
                  type: "spring",
                  bounce: 0.4,
                }}
                className="flex flex-col justify-center"
              >
                <motion.div
                  initial={{ scale: 0.9 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="mb-6 bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text"
                >
                  <h2 className="text-transparent text-4xl font-bold">
                    Powerful Analytics Dashboard
                  </h2>
                </motion.div>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-8 text-xl text-gray-600"
                >
                  Get deep insights into your social media performance with our
                  comprehensive analytics dashboard. Track engagement, reach,
                  and growth across all platforms.
                </motion.p>
                {/* <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/main/analytics"
                    className="group relative inline-flex w-fit items-center overflow-hidden rounded-full bg-gradient-to-r from-purple-600 to-pink-500 px-8 py-3 text-white transition-all duration-300"
                  >
                    <span className="absolute left-0 h-full w-0 bg-gradient-to-r from-pink-500 to-purple-600 transition-all duration-300 group-hover:w-full" />
                    <span className="relative flex items-center">
                      View Analytics
                      <motion.span
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <FiArrowRight className="ml-2" />
                      </motion.span>
                    </span>
                  </Link>
                </motion.div> */}
              </motion.div>
              <motion.div
                initial={{ opacity: 0, rotateY: 90 }}
                whileInView={{ opacity: 1, rotateY: 0 }}
                transition={{ duration: 0.8 }}
                className="relative col-span-2"
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="rounded-xl shadow-2xl"
                >
                  <img
                    src={Landing3Img}
                    alt="Analytics Dashboard"
                    className="rounded-xl"
                  />
                  <motion.div
                    className="absolute inset-0 rounded-xl"
                    animate={{
                      boxShadow: [
                        "0 0 0 0 rgba(127, 3, 226, 0)",
                        "0 0 0 10px rgba(127, 3, 226, 0.1)",
                        "0 0 0 20px rgba(127, 3, 226, 0)",
                      ],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Subscription Management Section */}
        {/* <section className="relative overflow-hidden bg-gradient-to-b from-purple-50 to-white py-20">
          <motion.div
            className="absolute inset-0"
            animate={{
              background: [
                "linear-gradient(315deg, rgba(127, 3, 226, 0.1) 0%, transparent 100%)",
                "linear-gradient(315deg, transparent 0%, rgba(161, 59, 242, 0.1) 100%)",
              ],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
          <div className="container relative mx-auto px-6">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, rotateY: 90 }}
                whileInView={{ opacity: 1, rotateY: 0 }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="rounded-xl shadow-2xl"
                >
                  <img
                    src={SubscriptionImg}
                    alt="Subscription Management Interface"
                    className="rounded-xl"
                  />
                  <motion.div
                    className="absolute inset-0 rounded-xl"
                    animate={{
                      boxShadow: [
                        "0 0 0 0 rgba(127, 3, 226, 0)",
                        "0 0 0 10px rgba(127, 3, 226, 0.1)",
                        "0 0 0 20px rgba(127, 3, 226, 0)",
                      ],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.8,
                  type: "spring",
                  bounce: 0.4,
                }}
                className="flex flex-col justify-center"
              >
                <motion.div
                  initial={{ scale: 0.9 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="mb-6 bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text"
                >
                  <h2 className="text-transparent text-4xl font-bold">
                    Flexible Subscription Management
                  </h2>
                </motion.div>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-8 text-xl text-gray-700"
                >
                  Manage your subscriptions with ease and flexibility. Choose
                  the plan that suits your needs.
                </motion.p>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/main/profile/subscription-billing"
                    className="group relative inline-flex w-fit items-center overflow-hidden rounded-full bg-gradient-to-r from-purple-600 to-pink-500 px-8 py-3 text-white transition-all duration-300"
                  >
                    <span className="absolute left-0 h-full w-0 bg-gradient-to-r from-pink-500 to-purple-600 transition-all duration-300 group-hover:w-full" />
                    <span className="relative flex items-center">
                      Manage Subscription
                      <motion.span
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <FiArrowRight className="ml-2" />
                      </motion.span>
                    </span>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section> */}

        {/* Sign-In Section */}
        {/* <section className="relative overflow-hidden bg-gradient-to-b from-white to-purple-50 py-20">
          <motion.div
            className="absolute inset-0"
            animate={{
              background: [
                "linear-gradient(45deg, rgba(127, 3, 226, 0.1) 0%, transparent 100%)",
                "linear-gradient(45deg, transparent 0%, rgba(161, 59, 242, 0.1) 100%)",
              ],
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              repeatType: "reverse",
            }}
          />
          <div className="container relative mx-auto px-6 w-full md:w-[85%]">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
              <motion.div
                initial={{ opacity: 0, rotateY: 90 }}
                whileInView={{ opacity: 1, rotateY: 0 }}
                transition={{ duration: 0.8 }}
                className="relative"
              >
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="rounded-xl shadow-2xl"
                >
                  <img
                    src={SignInImg}
                    alt="Sign-In Interface"
                    className="rounded-xl"
                  />
                  <motion.div
                    className="absolute inset-0 rounded-xl"
                    animate={{
                      boxShadow: [
                        "0 0 0 0 rgba(127, 3, 226, 0)",
                        "0 0 0 10px rgba(127, 3, 226, 0.1)",
                        "0 0 0 20px rgba(127, 3, 226, 0)",
                      ],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </motion.div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, x: -100 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.8,
                  type: "spring",
                  bounce: 0.4,
                }}
                className="flex flex-col justify-center"
              >
                <motion.div
                  initial={{ scale: 0.9 }}
                  whileInView={{ scale: 1 }}
                  transition={{ duration: 0.5 }}
                  className="mb-6 bg-gradient-to-r from-purple-600 to-pink-500 bg-clip-text"
                >
                  <h2 className="text-transparent text-4xl font-bold">
                    Secure Sign-In
                  </h2>
                </motion.div>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="mb-8 text-xl text-gray-700"
                >
                  Access your account securely with our advanced sign-in
                  options.
                </motion.p>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/auth/sign-in"
                    className="group relative inline-flex w-fit items-center overflow-hidden rounded-full bg-gradient-to-r from-purple-600 to-pink-500 px-8 py-3 text-white transition-all duration-300"
                  >
                    <span className="absolute left-0 h-full w-0 bg-gradient-to-r from-pink-500 to-purple-600 transition-all duration-300 group-hover:w-full" />
                    <span className="relative flex items-center">
                      Sign In
                      <motion.span
                        animate={{ x: [0, 5, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <FiArrowRight className="ml-2" />
                      </motion.span>
                    </span>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section> */}
        {/* Pricing Section */}
        <SubscriptionPlans />
        {/* Testimonials Section */}
        <section className="bg-gradient-to-b from-white to-purple-50 py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-16 text-center"
            >
              <h2 className="mb-4 text-4xl font-bold text-gray-900">
                Trusted by Thousands
              </h2>
              <p className="mx-auto max-w-2xl text-xl text-gray-600">
                See what our customers have to say about their experience
              </p>
            </motion.div>

            <div className="grid gap-8 md:grid-cols-3">
              {testimonials.map((testimonial, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="rounded-lg bg-white p-8 shadow-lg"
                >
                  <p className="mb-6 italic text-gray-600">
                    "{testimonial.quote}"
                  </p>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {testimonial.author}
                    </p>
                    <p className="text-gray-600">{testimonial.role}</p>
                    <p className="text-purple-600">{testimonial.company}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
        {/* CTA Section */}
        <section className="bg-gradient-to-r from-[#7F03E2] to-[#A13BF2] py-20">
          <div className="container mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center text-white"
            >
              <h2 className="mb-4 text-4xl font-bold">
                Ready to Transform Your Social Media Strategy?
              </h2>
              <p className="mb-8 text-xl opacity-90">
                Join thousands of successful businesses managing their social
                media with us
              </p>
              <Link
                to="/main/default"
                className="inline-block rounded-full bg-white px-8 py-3 font-semibold text-purple-600 transition duration-300 hover:shadow-lg"
              >
                Get Started Free
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default LandingPage;
